export type GuestSelectionOption = {
  label: string;
  value: string;
};

const DEFAULT_ADULT_GUESTS = [2];
const CHILD_GUEST_COUNTS = [1, 2, 3];
const DEFAULT_CHILD_AGE = 10;

export const toChildGuestValue = (count: number) => `2+${count}`;

export function parseChildGuestValue(value: string) {
  const match = value.match(/^2\+([1-9]\d*)$/);
  if (!match) return null;
  return Number(match[1]);
}

export function formatGuestSelectionValue(value: string) {
  const childCount = parseChildGuestValue(value);
  if (childCount !== null) return toChildGuestValue(childCount);
  const adultCount = Number(value);
  return Number.isFinite(adultCount) ? `${adultCount} чел` : value;
}

export function toGuestSelectionValues(guests?: number[] | null, guestsChild?: number[] | null) {
  return [
    ...(guests ?? []).map(String),
    ...(guestsChild ?? []).map(toChildGuestValue),
  ];
}

export function buildGuestSelectionOptions(
  guests?: number[] | null,
  guestsChild?: number[] | null,
  options: { includeAllChildOptions?: boolean; fallbackAdults?: number[] } = {},
): GuestSelectionOption[] {
  const adultGuests = guests?.length ? guests : options.fallbackAdults ?? DEFAULT_ADULT_GUESTS;
  const childGuests = options.includeAllChildOptions ? CHILD_GUEST_COUNTS : guestsChild ?? [];
  const values = Array.from(
    new Set([
      ...adultGuests.map(String),
      ...childGuests
        .filter((count) => CHILD_GUEST_COUNTS.includes(count))
        .map(toChildGuestValue),
    ]),
  );

  return values.map((value) => ({ value, label: formatGuestSelectionValue(value) }));
}

export function splitGuestSelections(values: string[]) {
  const adultGuests = new Set<number>();
  const childGuests = new Set<number>();

  values.forEach((value) => {
    const childCount = parseChildGuestValue(value);
    if (childCount !== null) {
      if (CHILD_GUEST_COUNTS.includes(childCount)) childGuests.add(childCount);
      return;
    }

    const adultCount = Number(value);
    if (Number.isInteger(adultCount) && adultCount > 0) adultGuests.add(adultCount);
  });

  const guests = Array.from(adultGuests).sort((left, right) => left - right);
  const guestsChild = Array.from(childGuests).sort((left, right) => left - right);

  return {
    guests: guests.length ? guests : null,
    guests_child: guestsChild.length ? guestsChild : null,
  };
}

export function buildAnalyticsGuestsPayload(value: string) {
  const childCount = parseChildGuestValue(value);
  if (childCount !== null) {
    return {
      guests: 2,
      guests_child: Array.from({ length: childCount }, () => DEFAULT_CHILD_AGE),
    };
  }

  const adultCount = Number(value);
  return {
    guests: Number.isInteger(adultCount) && adultCount > 0 ? adultCount : DEFAULT_ADULT_GUESTS[0],
  };
}

export function formatGuests(guests?: number[] | null, guestsChild?: number[] | null) {
  const values = toGuestSelectionValues(guests, guestsChild);
  return values.length ? values.map(formatGuestSelectionValue).join(", ") : "Не задан";
}
